import { Box, Divider, Link, Typography } from "@mui/material";
import { usePageMeta } from "../hooks/usePageMeta";

export function PrivacyPage() {
    usePageMeta({
        title: 'Privacy Policy',
        description: 'Privacy policy for ColbrenSolutions LLC AI-powered communication services.',
    });

    return (
        <>
            <Box sx={{ mb: 4 }}>
                <Typography variant='h3' sx={{ fontWeight: 700, mb: 1 }}>Privacy Policy</Typography>
                <Typography variant='body2' color='text.secondary'>ColbrenSolutions LLC — Last updated: May 15, 2026</Typography>
            </Box>

            <Box sx={{ maxWidth: 760 }}>

                <Section title="Overview">
                    ColbrenSolutions LLC ("Colbren", "we", "us") operates AI-powered communication services on behalf of local
                    businesses, including automated phone ordering, appointment booking, and service scheduling. This policy
                    explains how we collect, use, and protect your information.
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Information We Collect">
                    When you interact with a Colbren-powered phone or messaging service, we may collect:
                    <List items={[
                        'Your phone number',
                        'Your name (if provided during the interaction)',
                        'Order, appointment, or service request details',
                        'The date and time of your interaction',
                    ]} />
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="How We Use Your Information">
                    We use your information solely to:
                    <List items={[
                        'Confirm and process your order, appointment, or service request',
                        'Send you SMS notifications related to your interaction (confirmations, reminders, status updates)',
                        'Notify the business on your behalf',
                    ]} />
                    <Typography variant='body1' sx={{ mt: 2, lineHeight: 1.8 }}>
                        We do not sell, rent, or share your personal information with third parties for marketing purposes.
                    </Typography>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="SMS Messaging">
                    By completing a phone interaction with a Colbren-powered service, you consent to receive SMS notifications
                    related to that interaction. Message frequency varies based on your activity.
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                            <strong>To opt out:</strong> Reply STOP to any message at any time
                        </Typography>
                        <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                            <strong>To opt back in:</strong> Reply START
                        </Typography>
                        <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                            <strong>For help:</strong> Reply HELP or contact us at{' '}
                            <Link href='mailto:info@colbrensolutions.com'>info@colbrensolutions.com</Link>
                        </Typography>
                    </Box>
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                        Message and data rates may apply.
                    </Typography>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Data Retention">
                    Your interaction data is retained for up to 12 months and then deleted, unless a longer retention period
                    is required by the business we serve or by law.
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Third-Party Services">
                    Our services use the following third-party platforms to operate:
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                            <strong>Twilio</strong> — phone and SMS delivery (
                            <Link href='https://twilio.com/legal/privacy' target='_blank' rel='noopener noreferrer'>
                                twilio.com/legal/privacy
                            </Link>
                            )
                        </Typography>
                        <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                            <strong>Microsoft Azure</strong> — cloud hosting and AI processing (
                            <Link href='https://microsoft.com/privacy' target='_blank' rel='noopener noreferrer'>
                                microsoft.com/privacy
                            </Link>
                            )
                        </Typography>
                    </Box>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Contact">
                    For privacy questions or data removal requests:
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant='body1'><strong>ColbrenSolutions LLC</strong></Typography>
                        <Typography variant='body1'>
                            Email: <Link href='mailto:info@colbrensolutions.com'>info@colbrensolutions.com</Link>
                        </Typography>
                        <Typography variant='body1'>
                            Web: <Link href='https://colbren.com/privacy' target='_blank' rel='noopener noreferrer'>
                                https://colbren.com/privacy
                            </Link>
                        </Typography>
                    </Box>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
                    This policy applies to SMS communications and phone interactions managed by ColbrenSolutions LLC on
                    behalf of its business clients.
                </Typography>

            </Box>
        </>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Box sx={{ mb: 0 }}>
            <Typography variant='h6' sx={{ fontWeight: 700, mb: 1.5 }}>{title}</Typography>
            {typeof children === 'string'
                ? <Typography variant='body1' sx={{ lineHeight: 1.8 }}>{children}</Typography>
                : children
            }
        </Box>
    );
}

function List({ items }: { items: string[] }) {
    return (
        <Box component='ul' sx={{ mt: 1, mb: 0, pl: 3 }}>
            {items.map(item => (
                <Box component='li' key={item} sx={{ mb: 0.5 }}>
                    <Typography variant='body1' sx={{ lineHeight: 1.8 }}>{item}</Typography>
                </Box>
            ))}
        </Box>
    );
}
